import { z } from 'zod';
import { cardCompareSchema } from './compare';
import { reportCardSchema } from './reports';

export { cardCompareSchema };

export const CARD_METRIC_UNITS = [
    'count',
    'currency',
    'percentage',
    'duration_seconds',
    'ratio',
] as const;

const calendarDateSchema = z.iso.date();

export const cardMetricSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    unit: z.enum(CARD_METRIC_UNITS),
    lowerIsBetter: z.boolean().optional(),
    value: z.number().nullable(),
    ratio: z.object({ num: z.number(), den: z.number() }).optional(),
    compare: cardCompareSchema.optional(),
});

export const cardRowSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    values: z.record(
        z.string(),
        z.object({
            value: z.number().nullable(),
            compare: cardCompareSchema.optional(),
        })
    ),
});

export const cardSeriesPointSchema = z.object({
    date: calendarDateSchema,
    values: z.record(z.string(), z.number().nullable()),
});

const cardFields = {
    kpi: z.string().min(1),
    title: z.string().min(1),
    subtitle: z.string(),
    primary: z.array(cardMetricSchema),
    others: z.array(cardMetricSchema),
    caveats: z.array(z.string().min(1)),
    warnings: z.array(z.string().min(1)),
    dataCompleteThrough: calendarDateSchema.optional(),
    unavailable: z.object({ reason: z.string().min(1) }).optional(),
};

export const CHOICES_CALL_KPI = 'choices';
export const CHOICE_TEXT_MAX = 40;

const choiceText = z.string().trim().min(1).max(CHOICE_TEXT_MAX);

export const choiceGroupSchema = z.object({
    label: choiceText,
    options: z.array(choiceText).min(1).max(8),
});

export const choiceGroupsSchema = z.array(choiceGroupSchema).min(1).max(3);

const choicesCardSchema = z.object({
    shape: z.literal('choices'),
    groups: choiceGroupsSchema,
});

export const cardSchema = z.discriminatedUnion('shape', [
    z.object({ shape: z.literal('value'), ...cardFields }),
    z.object({ shape: z.literal('comparison'), ...cardFields }),
    z.object({
        shape: z.literal('breakdown'),
        ...cardFields,
        rows: z.array(cardRowSchema),
    }),
    z.object({
        shape: z.literal('breakdown_comparison'),
        ...cardFields,
        rows: z.array(cardRowSchema),
    }),
    z.object({
        shape: z.literal('series'),
        ...cardFields,
        series: z.array(cardSeriesPointSchema),
    }),
    reportCardSchema,
    choicesCardSchema,
]);

export type CardMetricUnit = (typeof CARD_METRIC_UNITS)[number];
export type CardCompare = z.infer<typeof cardCompareSchema>;
export type CardMetric = z.infer<typeof cardMetricSchema>;
export type CardRow = z.infer<typeof cardRowSchema>;
export type CardSeriesPoint = z.infer<typeof cardSeriesPointSchema>;
export type Card = z.infer<typeof cardSchema>;
export type CardShape = Card['shape'];
export type ChoiceGroup = z.infer<typeof choiceGroupSchema>;
export type KpiCard = Exclude<Card, { shape: 'report' | 'choices' }>;
export type ReportCardData = Extract<Card, { shape: 'report' }>;
export type ChoicesCardData = Extract<Card, { shape: 'choices' }>;
